import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useIonAlert } from '@ionic/react';
import { Banknote, Check, FileText, Landmark, Upload, X } from 'lucide-react';
import { addMoney, formatMoney, isMoneyString, subMoney, toCents } from '@tcrd/shared';
import { Button, Card, cn, ErrorState, Notice, PaymentStatusBadge, SectionTitle, Spinner, TextField } from '@tcrd/ui';
import { Page } from '../../../components/Page';
import { useToast } from '../../../components/toast';
import { api, errorMessage, newIdempotencyKey } from '../../../lib/api';
import { openAuthenticatedFile } from '../../../lib/download';
import { fmtDateTime } from '../../../lib/format';
import { pickFile, toForm } from '../../../lib/media';
import { TX_METHOD, TX_STATUS, TX_TYPE, type PaymentDto } from '../../common/payments-types';

type Method = 'CASH' | 'TRANSFER' | 'MIXED';
const m = (v: string) => (isMoneyString(v || '0') ? v || '0' : '0');
const TIP_CHIPS = ['0', '100', '200', '300', '500'];

/** Registrar cobro: el conductor declara lo efectivamente recibido. */
export function CollectPaymentPage({ paymentIdParam }: { paymentIdParam?: boolean }) {
  const params = useParams<{ id: string }>();
  const history = useHistory();
  const qc = useQueryClient();
  const toast = useToast();
  const [alert] = useIonAlert();
  const path = paymentIdParam ? `/payments/${params.id}` : `/rides/${params.id}/payment`;
  const q = useQuery({ queryKey: ['payment', path], queryFn: () => api<PaymentDto>(path) });
  const [method, setMethod] = useState<Method>('CASH');
  const [cash, setCash] = useState('');
  const [transfer, setTransfer] = useState('');
  const [reference, setReference] = useState('');
  const [tip, setTip] = useState('0');
  const [change, setChange] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const idem = useRef(newIdempotencyKey());
  const p = q.data;

  const calc = useMemo(() => {
    if (!p) return null;
    const c = method === 'TRANSFER' ? '0' : m(cash);
    const t = method === 'CASH' ? '0' : m(transfer);
    const ch = method === 'TRANSFER' ? '0' : m(change);
    const received = subMoney(addMoney(c, t), ch);
    const expected = addMoney(p.balance, m(tip));
    const pending = subMoney(expected, received);
    return { c, t, ch, received, expected, pending, over: toCents(pending) < 0n };
  }, [p, method, cash, transfer, change, tip]);

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['payment'] }), qc.invalidateQueries({ queryKey: ['ride'] }), qc.invalidateQueries({ queryKey: ['payments'] })]);

  const submit = async () => {
    if (!p || !calc) return;
    setBusy(true);
    try {
      await api<PaymentDto>(`/payments/${p.id}/collect`, {
        body: { cash: calc.c !== '0' ? calc.c : undefined, transfer: calc.t !== '0' ? calc.t : undefined, transferReference: reference || undefined, changeReturned: calc.ch !== '0' ? calc.ch : undefined, tip: m(tip) !== '0' ? m(tip) : undefined, note: note || undefined },
        idempotencyKey: idem.current,
      });
      idem.current = newIdempotencyKey();
      setCash('');
      setTransfer('');
      setChange('');
      setTip('0');
      await refresh();
      toast('Cobro registrado', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };

  const confirmReport = (txId: string, received: boolean) =>
    api(`/payments/transactions/${txId}/confirm`, { body: { received } })
      .then(refresh)
      .then(() => toast(received ? 'Pago confirmado' : 'Pago marcado como no recibido'))
      .catch((e) => toast(errorMessage(e), 'danger'));

  const adjust = () =>
    alert({
      header: 'Ajuste',
      message: 'Descuento (negativo) aplica de inmediato. Cargo adicional (positivo) requiere aceptación del cliente.',
      inputs: [
        { name: 'amount', type: 'text', placeholder: 'Ej. -100.00 o 250.00' },
        { name: 'reason', type: 'text', placeholder: 'Motivo' },
      ],
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        { text: 'Guardar', handler: (v: { amount: string; reason: string }) => void api(`/payments/${p!.id}/adjustments`, { body: v }).then(refresh).catch((e) => toast(errorMessage(e), 'danger')) },
      ],
    });

  const uploadEvidence = async () => {
    const blob = await pickFile('image/jpeg,image/png,image/webp,application/pdf');
    if (!blob || !p) return;
    await api(`/payments/${p.id}/evidence`, { method: 'POST', form: toForm(blob, {}, 'comprobante') }).then(refresh).catch((e) => toast(errorMessage(e), 'danger'));
  };

  const reported = p?.transactions.filter((t) => t.status === 'REPORTED' && t.type === 'FARE') ?? [];
  const row = (label: string, value: string, strong = false) => (
    <div className={cn('flex justify-between py-1.5', strong && 'font-bold text-base')}>
      <span className={strong ? '' : 'text-[var(--text-muted)]'}>{label}</span>
      <span className="tabular-nums">{formatMoney(value)}</span>
    </div>
  );

  return (
    <Page title="Registrar cobro" back={p?.ride ? `/driver/rides/${p.ride.id}` : '/driver/payments'} onRefresh={() => q.refetch()}>
      {q.isLoading && <Spinner />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {p && calc && (
        <div className="flex flex-col gap-5">
          <Card className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[var(--text-muted)]">{p.ride ? `${p.ride.code} · ${p.customer.name}` : `Paquete · ${p.customer.name}`}</p>
                <p className="text-2xl font-extrabold tabular-nums">{formatMoney(p.balance)} <span className="text-sm font-normal text-[var(--text-muted)]">pendiente</span></p>
              </div>
              <PaymentStatusBadge status={p.status} />
            </div>
          </Card>

          {reported.map((t) => (
            <Card key={t.id} className="p-4 border-2 border-sky-400/60">
              <p className="text-sm">
                El cliente reporta <strong>{formatMoney(t.amount)}</strong> por {TX_METHOD[t.method ?? 'CASH']?.toLowerCase()}
                {t.reference ? ` (ref. ${t.reference})` : ''}. {t.note ? `“${t.note}”` : ''}
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">Confírmalo solo si verificaste que el dinero llegó.</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button size="sm" variant="secondary" icon={<X className="size-4" />} onClick={() => confirmReport(t.id, false)}>No recibido</Button>
                <Button size="sm" variant="accent" icon={<Check className="size-4" />} onClick={() => confirmReport(t.id, true)}>Confirmar</Button>
              </div>
            </Card>
          ))}

          {p.canCollect && toCents(p.balance) > 0n && (
            <Card className="p-5 flex flex-col gap-4">
              <SectionTitle title="Dinero recibido" />
              <div role="radiogroup" aria-label="Método" className="grid grid-cols-3 gap-2">
                {([
                  ['CASH', 'Efectivo', Banknote],
                  ['TRANSFER', 'Transferencia', Landmark],
                  ['MIXED', 'Mixto', FileText],
                ] as const).map(([k, l, Icon]) => (
                  <button key={k} role="radio" aria-checked={method === k} onClick={() => setMethod(k)} className={cn('tap rounded-2xl border py-3 flex flex-col items-center gap-1 text-sm font-medium', method === k ? 'border-brand-red bg-brand-red/5' : 'border-[var(--border)]')}>
                    <Icon className="size-5" aria-hidden /> {l}
                  </button>
                ))}
              </div>
              {method !== 'TRANSFER' && <TextField label="Efectivo recibido (RD$)" inputMode="decimal" value={cash} onChange={(e) => setCash(e.target.value)} placeholder={addMoney(p.balance, m(tip))} />}
              {method !== 'CASH' && (
                <div className="grid grid-cols-2 gap-3">
                  <TextField label="Transferencia (RD$)" inputMode="decimal" value={transfer} onChange={(e) => setTransfer(e.target.value)} />
                  <TextField label="Referencia" value={reference} onChange={(e) => setReference(e.target.value)} />
                </div>
              )}
              <div>
                <p className="text-sm font-medium mb-2">Propina (solo si el cliente la entregó)</p>
                <div className="flex gap-2 flex-wrap">
                  {TIP_CHIPS.map((c) => (
                    <button key={c} onClick={() => setTip(c)} aria-pressed={tip === c} className={cn('tap rounded-full border px-4 text-sm', tip === c ? 'border-brand-red bg-brand-red/10 font-semibold' : 'border-[var(--border)]')}>
                      {c === '0' ? 'Sin propina' : formatMoney(c)}
                    </button>
                  ))}
                  <input aria-label="Otra propina" inputMode="decimal" value={TIP_CHIPS.includes(tip) ? '' : tip} onChange={(e) => setTip(e.target.value)} placeholder="Otra" className="w-24 h-11 rounded-full border border-[var(--border)] bg-[var(--surface)] px-4 text-sm" />
                </div>
              </div>
              {method !== 'TRANSFER' && <TextField label="Cambio devuelto (RD$)" inputMode="decimal" value={change} onChange={(e) => setChange(e.target.value)} hint="Si el cliente pagó con un billete mayor" />}
              <TextField label="Observaciones" value={note} onChange={(e) => setNote(e.target.value)} />

              <div className="rounded-2xl bg-[var(--surface-2)] px-4 py-3 text-sm">
                {row('Tarifa acordada', p.totalDue)}
                {toCents(p.farePaid) > 0n && row('Ya cobrado', p.farePaid)}
                {row('Propina', m(tip))}
                {row('Total esperado', calc.expected)}
                {row('Recibido', calc.received)}
                {row('Saldo pendiente', calc.over ? '0' : calc.pending, true)}
              </div>
              {calc.over && <Notice tone="warning">Recibiste {formatMoney(subMoney('0', calc.pending))} de más: regístralo como propina o como cambio devuelto.</Notice>}
              <Button variant="accent" size="lg" loading={busy} disabled={toCents(calc.received) <= 0n || calc.over} onClick={submit}>
                {toCents(calc.pending) > 0n ? 'Registrar pago parcial' : 'Registrar cobro'}
              </Button>
              <Button variant="ghost" onClick={() => history.push('/driver/dashboard')}>Cobrar después</Button>
            </Card>
          )}

          <Card className="p-5">
            <SectionTitle title="Movimientos" action={p.canCollect && <Button size="sm" variant="secondary" onClick={adjust}>Ajuste</Button>} />
            {p.transactions.length === 0 && <p className="text-sm text-[var(--text-muted)]">Sin movimientos.</p>}
            <ul className="divide-y divide-[var(--border)]">
              {p.transactions.map((t) => (
                <li key={t.id} className={cn('py-2.5 text-sm flex items-center justify-between gap-3', ['VOIDED', 'REJECTED'].includes(t.status) && 'opacity-50 line-through')}>
                  <span>
                    {TX_TYPE[t.type]}{t.method ? ` · ${TX_METHOD[t.method]}` : ''}{t.reference ? ` · ${t.reference}` : ''}
                    <span className="block text-xs text-[var(--text-muted)]">{fmtDateTime(t.receivedAt)} · {TX_STATUS[t.status]}{t.note ? ` · ${t.note}` : ''}</span>
                  </span>
                  <span className="tabular-nums font-medium">{t.type === 'CHANGE_RETURNED' ? '−' : ''}{formatMoney(t.amount.replace('-', ''))}{t.amount.startsWith('-') ? ' (desc.)' : ''}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" icon={<Upload className="size-4" />} onClick={uploadEvidence}>Adjuntar comprobante</Button>
              <Button size="sm" variant="secondary" icon={<FileText className="size-4" />} onClick={() => openAuthenticatedFile(`/payments/${p.id}/receipt.pdf`, `comprobante-${p.ride?.code ?? p.id.slice(0, 8)}.pdf`).catch((e) => toast(errorMessage(e), 'danger'))}>Ver comprobante</Button>
              {p.ride && <Link to={`/driver/rides/${p.ride.id}`}><Button size="sm" variant="ghost">Ver viaje</Button></Link>}
            </div>
            {p.evidences.length > 0 && <p className="mt-3 text-xs text-[var(--text-muted)]">{p.evidences.length} comprobante(s) adjunto(s)</p>}
          </Card>
        </div>
      )}
    </Page>
  );
}
